import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, House, Buildings, Tree, HardHat, Question } from "@phosphor-icons/react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api/booking`;
export const http = axios.create();
export const usd = (n) => `$${Math.round(n || 0).toLocaleString()}`;
export const usd2 = (n) => `$${(n || 0).toFixed(2)}`;
export const PHONE = "828-929-1444";
export const EMAIL = "bookings@goreelty.com";

export const field = "w-full rounded-lg border border-[#E9DDC6] bg-white px-3 py-2.5 text-sm text-[#1A1715] focus:outline-none focus:ring-2 focus:ring-[#B08D57] transition";

export const TYPES = [
  { id: "residential", t: "Residential", d: "House, condo, townhome", entry: true, icon: House },
  { id: "commercial", t: "Commercial", d: "Office, retail, mixed use", entry: true, icon: Buildings },
  { id: "industrial", t: "Industrial", d: "Warehouse, facility", entry: true, icon: HardHat },
  { id: "land", t: "Land & Acreage", d: "Raw land, lots, sites", entry: false, icon: Tree },
  { id: "new_construction", t: "New Construction", d: "Build in progress", entry: true, icon: HardHat },
  { id: "unsure", t: "Not sure", d: "We'll help you pick", entry: true, icon: Question },
];

export const STEPS = [
  { k: "address", t: "Location" },
  { k: "package", t: "Package" },
  { k: "property", t: "Property" },
  { k: "schedule", t: "Schedule" },
  { k: "details", t: "Details" },
];

// Does an à-la-carte service apply to this property type? (land → drone only)
export function svcApplies(cfg, key, ptype) {
  const svc = (cfg?.services || []).find((s) => s.key === key);
  if (!svc) return false;
  const t = svc.types || [];
  return t.includes("all") || t.includes(ptype) || !ptype;
}

/* Mechanical odometer: rolls to a new dollar value, animated in cents (shows $X.XX). */
export function Odometer({ value, className }) {
  const [display, setDisplay] = useState(Math.round((value || 0) * 100));
  const ref = useRef(Math.round((value || 0) * 100));
  useEffect(() => {
    const to = Math.round((value || 0) * 100), from = ref.current;
    if (from === to) return;
    if (!from) { setDisplay(to); ref.current = to; return; }
    const start = performance.now(); const dur = 520; let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick); else ref.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className={className} style={{ fontVariantNumeric: "tabular-nums" }}>{usd2(display / 100)}</span>;
}

/* Free OpenStreetMap embed that pinpoints the property (no API key needed).
   We hide OSM's default marker and overlay our own oxblood pin at the centred point. */
export function LocationMap({ lat, lon, label }) {
  if (lat == null || lon == null) return null;
  const d = 0.004;
  const bbox = `${lon - d},${lat - d},${lon + d},${lat + d}`;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik`;
  return (
    <div className="relative rounded-lg border border-[#E9DDC6] bg-[#FAF9F6] overflow-hidden h-40 sm:h-48" data-testid="rb-map">
      {/* iframe is taller than the frame so OSM's bottom link bar (report a problem / donate) is cropped off */}
      <iframe title="Property location" src={src} loading="lazy" className="absolute inset-x-0 top-0 w-full h-[calc(100%+46px)] sm:h-[calc(100%+26px)]" style={{ border: 0 }} />
      {/* Oxblood pin — tip anchored at the map centre (offset for the cropped strip) */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 mt-[23px] sm:mt-[13px] -translate-x-1/2 -translate-y-full">
        <MapPin size={30} weight="fill" className="text-[#7E1119] drop-shadow-[0_2px_3px_rgba(0,0,0,0.35)]" />
      </div>
      {label && (
        <div className="absolute top-2 left-2 flex items-center gap-1 text-[11px] font-medium bg-white/92 border border-[#E9DDC6] rounded px-2 py-0.5 text-[#4A0000] max-w-[75%] truncate">
          <MapPin size={11} weight="fill" className="text-[#7E1119] shrink-0" />{label}
        </div>
      )}
    </div>
  );
}
